import * as openRouterAdapter from './openRouterToElevenLabsAdapter';

interface ModerationResult {
  safe: boolean;
  translatedContent: string;
  originalContent: string;
  moderationReason?: string;
  confidence: number;
}

interface IntentTranslation {
  intent: string;
  context: string;
  safeVersion: string;
}

class ContentModerationService {
  private readonly HARMFUL_PATTERNS = [
    // Personal information patterns
    /\b\d{3}-\d{2}-\d{4}\b/g, // SSN
    /\b\d{16}\b/g, // Credit card
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Email
    /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, // Phone
    
    // Harmful intent patterns (will be checked semantically)
    /\b(kill|harm|hurt|attack|destroy)\s+(myself|yourself|others?|everyone)\b/gi,
    /\b(hate|despise|eliminate)\s+(all|every)\s+\w+/gi,
  ];

  private readonly SAFE_REPLACEMENTS: Record<string, string> = {
    'kill': 'defeat',
    'murder': 'overcome',
    'suicide': 'sacrifice',
    'hate': 'oppose',
    'destroy': 'challenge',
    'harm': 'confront',
    'hurt': 'face',
    'attack': 'engage',
    'die': 'fall',
    'death': 'defeat',
  };

  /**
   * Moderate user-generated content before it's shown to others
   */
  async moderateContent(
    content: string,
    context: 'chat' | 'choice' | 'name' | 'description',
    userId?: string
  ): Promise<ModerationResult> {
    try {
      // First pass: Check for obvious patterns
      const quickCheck = this.quickPatternCheck(content);
      if (!quickCheck.safe) {
        return quickCheck;
      }

      // Second pass: Semantic analysis for harmful intent
      const semanticCheck = await this.semanticAnalysis(content, context);
      if (!semanticCheck.safe) {
        return semanticCheck;
      }

      // Third pass: Translate intent to safe game-appropriate version
      const translated = await this.translateToGameSafe(content, context);
      
      return {
        safe: true,
        translatedContent: translated.safeVersion,
        originalContent: content,
        confidence: 0.95
      };
    } catch (error) {
      console.error('[ContentModeration] Error moderating content:', error);
      // In case of error, be conservative and block
      return {
        safe: false,
        translatedContent: this.getDefaultSafeMessage(context),
        originalContent: content,
        moderationReason: 'Moderation service unavailable',
        confidence: 0
      };
    }
  }

  /**
   * Quick pattern-based check for obvious harmful content
   */
  private quickPatternCheck(content: string): ModerationResult {
    // Check for personal information
    for (const pattern of this.HARMFUL_PATTERNS) {
      if (pattern.test(content)) {
        return {
          safe: false,
          translatedContent: '[Content removed for privacy]',
          originalContent: content,
          moderationReason: 'Contains personal information',
          confidence: 1.0
        };
      }
    }

    // Check for explicit harmful keywords in non-game context
    const lowerContent = content.toLowerCase();
    const harmfulKeywords = ['suicide', 'self-harm', 'kill myself', 'end my life'];
    
    for (const keyword of harmfulKeywords) {
      if (lowerContent.includes(keyword)) {
        return {
          safe: false,
          translatedContent: this.translateHarmfulIntent(content),
          originalContent: content,
          moderationReason: 'Contains harmful intent',
          confidence: 0.9
        };
      }
    }

    return {
      safe: true,
      translatedContent: content,
      originalContent: content,
      confidence: 0.8
    };
  }

  /**
   * Use AI to analyze semantic meaning for harmful intent
   */
  private async semanticAnalysis(
    content: string,
    context: 'chat' | 'choice' | 'name' | 'description'
  ): Promise<ModerationResult> {
    try {
      const prompt = `
You are a content moderation system for a cyberpunk fantasy MMORPG.
Analyze the following ${context} content for harmful intent:

"${content}"

Determine if this contains:
1. Real-world violence or self-harm intent
2. Harassment or hate speech
3. Personal information sharing
4. Attempts to break the game narrative
5. Inappropriate content for a T-rated game

Respond with JSON:
{
  "safe": boolean,
  "reason": "brief explanation if unsafe",
  "gameContext": "how this relates to game mechanics"
}`;

      // Use the adapter to get a response
      const response = await openRouterAdapter.generateChatResponse(
        prompt,
        [],
        { narrative: '', choices: [], image_prompt: '' },
        { name: 'moderator', description: 'content moderation' },
        null
      );
      
      // Parse the response
      let moderationResult: any = { safe: true };
      try {
        const responseText = response.dialogues[0]?.message || '{}';
        moderationResult = JSON.parse(responseText);
      } catch {
        // If parsing fails, assume safe
        moderationResult = { safe: true };
      }
      
      if (!moderationResult.safe) {
        return {
          safe: false,
          translatedContent: this.translateToSafeGameAction(content, context),
          originalContent: content,
          moderationReason: moderationResult.reason,
          confidence: 0.85
        };
      }

      return {
        safe: true,
        translatedContent: content,
        originalContent: content,
        confidence: 0.85
      };
    } catch (error) {
      console.error('[ContentModeration] Semantic analysis failed:', error);
      // Default to safe but monitor
      return {
        safe: true,
        translatedContent: content,
        originalContent: content,
        confidence: 0.5
      };
    }
  }

  /**
   * Translate user intent to game-appropriate safe version
   */
  private async translateToGameSafe(
    content: string,
    context: 'chat' | 'choice' | 'name' | 'description'
  ): Promise<IntentTranslation> {
    try {
      // For short content like names, do simple replacement
      if (context === 'name' || content.length < 50) {
        return {
          intent: content,
          context: context,
          safeVersion: this.simpleTranslation(content)
        };
      }

      const prompt = `
You are translating player intent in a cyberpunk fantasy MMORPG.
Convert this ${context} into game-appropriate language:

Original: "${content}"

Rules:
1. Preserve the player's game intent (combat, exploration, roleplay)
2. Remove any real-world harmful content
3. Translate violence to game mechanics (PvP combat, boss fights)
4. Keep it immersive and atmospheric
5. Maximum 2 sentences

Respond with the translated version only.`;

      // Use the adapter to get a translation
      const response = await openRouterAdapter.generateChatResponse(
        prompt,
        [],
        { narrative: '', choices: [], image_prompt: '' },
        { name: 'translator', description: 'content translation' },
        null
      );
      
      const translated = response.dialogues[0]?.message || '';
      
      return {
        intent: content,
        context: context,
        safeVersion: translated || this.simpleTranslation(content)
      };
    } catch (error) {
      console.error('[ContentModeration] Translation failed:', error);
      return {
        intent: content,
        context: context,
        safeVersion: this.simpleTranslation(content)
      };
    }
  }

  /**
   * Simple word replacement for quick translation
   */
  private simpleTranslation(content: string): string {
    let translated = content;
    
    for (const [harmful, safe] of Object.entries(this.SAFE_REPLACEMENTS)) {
      const regex = new RegExp(`\\b${harmful}\\b`, 'gi');
      translated = translated.replace(regex, safe);
    }
    
    return translated;
  }

  /**
   * Translate harmful intent to game-safe alternative
   */
  private translateHarmfulIntent(content: string): string {
    const gameAlternatives = [
      "I seek to challenge the void",
      "I will face the digital darkness",
      "I choose to confront my fate",
      "I'll enter the cyberspace trials",
      "I wish to test my limits in the arena"
    ];
    
    return gameAlternatives[Math.floor(Math.random() * gameAlternatives.length)];
  }

  /**
   * Convert harmful content to safe game action
   */
  private translateToSafeGameAction(
    content: string,
    context: 'chat' | 'choice' | 'name' | 'description'
  ): string {
    const safeVersions: Record<string, string[]> = {
      chat: [
        "I challenge you to a duel!",
        "Let's settle this in the arena.",
        "My avatar stands ready for combat.",
        "I seek a worthy opponent."
      ],
      choice: [
        "Engage in combat",
        "Challenge the adversary",
        "Enter the PvP arena",
        "Face the digital trial"
      ],
      name: [
        "CyberWarrior",
        "DigitalKnight",
        "NeonChallenger",
        "VoidSeeker"
      ],
      description: [
        "A warrior seeking challenges in the digital realm",
        "A player ready for epic battles",
        "An adventurer exploring the cyber frontier",
        "A hero facing the void"
      ]
    };
    
    const options = safeVersions[context] || safeVersions.chat;
    return options[Math.floor(Math.random() * options.length)];
  }

  /**
   * Get default safe message for context
   */
  private getDefaultSafeMessage(context: 'chat' | 'choice' | 'name' | 'description'): string {
    const defaults: Record<string, string> = {
      chat: "Let's adventure together!",
      choice: "Continue the journey",
      name: "Dreamer",
      description: "A mysterious traveler"
    };
    
    return defaults[context] || "...";
  }

  /**
   * Batch moderate multiple pieces of content
   */
  async moderateBatch(
    contents: Array<{ content: string; context: 'chat' | 'choice' | 'name' | 'description' }>,
    userId?: string
  ): Promise<ModerationResult[]> {
    return Promise.all(
      contents.map(({ content, context }) => 
        this.moderateContent(content, context, userId)
      )
    );
  }

  /**
   * Check if a username is appropriate
   */
  async isUsernameAppropriate(username: string): Promise<boolean> {
    const result = await this.moderateContent(username, 'name');
    return result.safe && result.confidence > 0.7;
  }

  /**
   * Sanitize chat message before sending
   */
  async sanitizeChatMessage(message: string, userId?: string): Promise<string> {
    const result = await this.moderateContent(message, 'chat', userId);
    return result.translatedContent;
  }

  /**
   * Validate and translate player choice
   */
  async validatePlayerChoice(choice: string, userId?: string): Promise<string> {
    const result = await this.moderateContent(choice, 'choice', userId);
    return result.translatedContent;
  }

  /**
   * Moderate user input (alias for moderateContent)
   */
  async moderateUserInput(
    content: string,
    context: 'chat' | 'choice' | 'name' | 'description' = 'chat',
    gameContext?: any
  ): Promise<ModerationResult> {
    return this.moderateContent(content, context);
  }

  /**
   * Moderate AI response to ensure it's appropriate
   */
  async moderateAIResponse(
    response: string,
    context?: any
  ): Promise<{ safe: boolean; cleanedResponse?: string; reason?: string }> {
    const result = await this.moderateContent(response, 'chat');
    return {
      safe: result.safe,
      cleanedResponse: result.safe ? response : result.translatedContent,
      reason: result.moderationReason
    };
  }

  /**
   * Translate user intent to safe game actions
   */
  async translateUserIntent(
    intent: string,
    context?: any
  ): Promise<{ translatedIntent: string; category: string }> {
    const translated = await this.translateToGameSafe(intent, 'choice');
    
    // Determine category based on keywords
    let category = 'exploration';
    if (intent.toLowerCase().includes('fight') || intent.toLowerCase().includes('attack')) {
      category = 'combat';
    } else if (intent.toLowerCase().includes('talk') || intent.toLowerCase().includes('speak')) {
      category = 'dialogue';
    }
    
    return {
      translatedIntent: translated.safeVersion,
      category
    };
  }

  /**
   * Filter harmful content from text
   */
  filterHarmfulContent(content: string): { safe: boolean; filtered: string } {
    if (!content) {
      return { safe: true, filtered: '' };
    }
    
    let filtered = content;
    let safe = true;
    
    // Check for harmful patterns
    for (const pattern of this.HARMFUL_PATTERNS) {
      if (pattern.test(content)) {
        safe = false;
        filtered = content.replace(pattern, '[FILTERED]');
      }
    }
    
    // Check for harmful words
    const harmfulWords = Object.keys(this.SAFE_REPLACEMENTS);
    for (const word of harmfulWords) {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      if (regex.test(content.toLowerCase())) {
        safe = false;
        filtered = filtered.replace(regex, this.SAFE_REPLACEMENTS[word]);
      }
    }
    
    return { safe, filtered };
  }

  /**
   * Get safety rating for content
   */
  getSafetyRating(content: string): { rating: 'safe' | 'questionable' | 'unsafe'; score: number } {
    if (!content) {
      return { rating: 'safe', score: 1.0 };
    }
    
    let score = 1.0;
    
    // Check for harmful patterns
    for (const pattern of this.HARMFUL_PATTERNS) {
      if (pattern.test(content)) {
        score -= 0.5;
      }
    }
    
    // Check for harmful words
    const harmfulWords = Object.keys(this.SAFE_REPLACEMENTS);
    for (const word of harmfulWords) {
      if (content.toLowerCase().includes(word)) {
        score -= 0.2;
      }
    }
    
    // Determine rating based on score
    let rating: 'safe' | 'questionable' | 'unsafe';
    if (score >= 0.7) {
      rating = 'safe';
    } else if (score >= 0.4) {
      rating = 'questionable';
    } else {
      rating = 'unsafe';
    }
    
    return { rating, score: Math.max(0, Math.min(1, score)) };
  }
}

export const contentModerationService = new ContentModerationService();